// lib/arena-ticker-content-store.ts
//
// There is exactly one ticker bar in the app — rendered by
// global-ticker-client.tsx, pinned to the bottom of the viewport on every
// page. live-arena.tsx (mounted only on /vote) has no way to reach into the
// layout's ticker directly, since it's a deeply nested client component and
// the ticker lives at the root layout level. This module is the bridge:
// live-arena.tsx publishes its matchup-local content (current match,
// standings, match-result/elimination/LCK items) here, and
// global-ticker-client.tsx subscribes and merges it with its own
// global content (shouts, mentions, push-notification catch-up) into the
// single rendered <ArenaTicker>.
//
// Plain external-store + useSyncExternalStore, same spirit as the simpler
// module-variable stores elsewhere (clock-offset.ts, known-build.ts) but with
// subscribe/notify since this one needs to actually trigger a re-render in a
// component that isn't an ancestor/descendant of the writer.

import type { TickerItem } from "@/app/components/arena-ticker";

export interface ArenaTickerContent {
  items: TickerItem[];
  matchNumber?: number;
  nameA?: string;
  nameB?: string;
  standings: { name: string; wins: number; losses: number }[];
  connStatus: "connected" | "reconnecting" | "disconnected";
}

const EMPTY_CONTENT: ArenaTickerContent = {
  items: [],
  standings: [],
  connStatus: "connected",
};

let content: ArenaTickerContent = EMPTY_CONTENT;
const listeners = new Set<() => void>();

export function setArenaTickerContent(next: ArenaTickerContent): void {
  content = next;
  listeners.forEach((l) => l());
}

// Called by live-arena.tsx on unmount (e.g. navigating away from /vote) so
// the global ticker falls back to showing only its own global content
// instead of a stale matchup's standings/match info.
export function clearArenaTickerContent(): void {
  content = EMPTY_CONTENT;
  listeners.forEach((l) => l());
}

export function subscribeArenaTickerContent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getArenaTickerContentSnapshot(): ArenaTickerContent {
  return content;
}

// Stable reference (not a fresh object each call) so React's
// useSyncExternalStore getServerSnapshot doesn't see a "changed" value on
// every render. Used as the SSR/first-paint snapshot — see the comment in
// global-ticker-client.tsx for why this must be the empty default rather
// than the live snapshot.
export function getEmptyArenaTickerContent(): ArenaTickerContent {
  return EMPTY_CONTENT;
}
